import * as React from 'react';
import { useAiSessionIdOps, useAsyncSessionAndMessages, useMessages } from "@me8eon/sessions_messages";
import { isDataAs, useLoadingFlag, useQuestionSuggestions } from "@me8eon/async";
import { useAiStreamingUiState, useDoAi } from "@me8eon/react_intent";
import { useSearchTextFilter } from "@me8eon/searchtext_filters";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AiAssistTemplate } from "./AiAssist.template";
import { MessageList } from "./organisms/MessageList/MessageList";
import { useTranslation } from "@me8eon/translation";
import { AiAssistantPromptArea } from "./organisms/AiAssistantPromptArea/AiAssistantPromptArea";
import { NoMessages } from "./organisms/NoMessages/NoMessages";
import { Suggestions } from "./organisms/Suggestions/Suggestions";
import {
    AgentSelectorTextAreaPlugin
} from "../../features/AiPrompt/plugins/AgentSelectorTextAreaPlugin/AgentSelectorTextAreaPlugin";
import { getParameterByName, isHigherEnv } from "../../util/common-util";
import { getSharedData } from "../../components/feedback/setShareData";
import { extractNewAiSource, useFeedback } from '@me8eon/feedback';
import { useJwtToken, useUserData } from '@me8eon/react_authentication';
import { createOpenAiClient } from "@me8eon/openai/src/openai.client";
import axios from "axios";
import { EnvironmentConfig } from "@me8eon/config";
import {
    ImageUploaderTextAreaPlugin
} from "../../features/AiPrompt/plugins/ImageUploaderTextAreaPlugin/ImageUploaderTextAreaPlugin";
import useSearchStore from '../../store/searchStore';
import { getLastObj, getSources, isWidgetMessage } from '../../util/message-utils';
import { defaultAction } from "@me8eon/messages";
import { MessageWithActionAndData } from "@me8eon/sessions_messages";
import { AiAssistContext } from '../../components/ai-assist-refactored/AiAssist.context';
import { PageToggle } from '@me8eon/page_toggle';
import ManualTask from '../../components/ai-assist-refactored/assistance-task/ManualTask';
import AssistanceTask from '../../components/ai-assist-refactored/assistance-task/AssistanceTask';
import { AiAssistantContextProvider } from '../../context/AiAssistantContext';
import Modal from '../../components/modal/Modal';
import TourStart from '../../components/tour-guide/TourStart';
import TourLangOptions from '../../components/tour-lang-options/TourLangOptions';

export default function AiAssistantPage() {
    const t = useTranslation();
    const [messages, setMessages] = useMessages();
    const [sessionId] = useAiSessionIdOps();
    const [isLoading] = useLoadingFlag();
    const [qs] = useQuestionSuggestions();
    const [streamingUiState] = useAiStreamingUiState();
    const [imageSuggestions, setImageSuggestions] = useState<string[]>([]);
    const [query, setQuery] = useSearchTextFilter();
    const handleAiQuery = useDoAi();
    const [asyncState] = useAsyncSessionAndMessages();
    const feedbackFn = useFeedback();
    const { currentAgent, quickActionType } = React.useContext(AiAssistContext);
    const { company, currentRole, featureFlagEnabled } = useUserData();
    const jwt = useJwtToken();
    const aiClient = createOpenAiClient({
        axios,
        baseURL: EnvironmentConfig.nodeService,
        apiKey: () => jwt()
    });
    const {
        displayedModal,
        clearQuestionData,
        currentTourId,
        showModal,
        firstTourResponse,
        startTour,
        setPrefData,
    } = useSearchStore();

    // URL-driven init on load: a shared conversation (reqId) takes priority over a deep-linked query (q, e.g. /ai?q=...).
    const autoQuerySubmitted = useRef(false);
    useEffect(() => {
        if (!(isDataAs(asyncState))) return;

        const reqId = getParameterByName("reqId");
        if (reqId) {
            const fetchData = async () => {
                try {
                    const sharedMessages: any[] = await getSharedData(reqId, jwt);
                    setMessages?.(sharedMessages);
                } catch (error) {
                    setMessages([]);
                    console.error("Failed to fetch shared data:", error);
                }
            };
            fetchData();
            return;
        }

        if (autoQuerySubmitted.current) return;
        const q = getParameterByName("q");
        if (!q) return;
        autoQuerySubmitted.current = true;
        handleAiQuery(q);
        const url = new URL(window.location.href);
        url.searchParams.delete("q");
        window.history.replaceState({}, "", url);
    }, [isDataAs(asyncState)]);

    const isNextThink = () => {
        return messages[messages.length - 1]?.role === "tool" && messages[messages.length - 2]?.role === "assistant";
    };

    const sendFeedback = (userMsg: string) => {
        const aiResp = getLastObj(messages, "assistant")?.message?.content as string || "";
        feedbackFn({
            ...(currentAgent === "itsupport" ? { answer: aiResp } : {}),
            source: "aiassist-query",
            messages: messages,
            sourceFromAnswer: quickActionType ? "IT Support Agent" : (isNextThink() ? "Nexthink Agent" : extractNewAiSource(aiResp)),
            sources: getSources(messages),
            query: userMsg,
            company: company || "unknown",
            role: currentRole || "unknown",
        });
    };

    useEffect(() => {
        if (messages[messages.length - 1]?.role === "assistant" || isNextThink()) {
            const userMsg = getLastObj(messages, "user");
            sendFeedback(userMsg?.message.content as string || "");
        }
    }, [messages]);

    const submitTextArea = useCallback(() => {
        if (isLoading) return;
        if (!query) return;
        handleAiQuery(query);
        setQuery("");
        clearQuestionData();
    }, [query, setQuery, handleAiQuery]);

    const onSuggestionClick = useCallback((suggestion: string) => {
        if (isLoading) return;
        if (!suggestion) return;
        handleAiQuery(suggestion);
    }, [isLoading, handleAiQuery]);

    const showImage = isHigherEnv() ? featureFlagEnabled : true;

    const headerToggle = useMemo(() => <PageToggle />, []);

    const promptArea = useMemo(() => (
        <AiAssistantPromptArea
            value={query}
            onChange={setQuery}
            listReverse={true}
            onSubmit={submitTextArea}
            imageSuggestions={imageSuggestions}
            plugins={[
                <ImageUploaderTextAreaPlugin
                    ai={aiClient}
                    setSuggestions={setImageSuggestions}
                />,
                ...(showImage ? [
                    <AgentSelectorTextAreaPlugin />
                ] : [])
            ]}
        />
    ), [query, setQuery, submitTextArea, aiClient, setImageSuggestions]);

    const visibleMessages = useMemo(() => {
        if (streamingUiState.status !== "streaming") {
            return messages;
        }

        const streamingMessage: MessageWithActionAndData = {
            role: "assistant",
            content: streamingUiState.content,
            agent: streamingUiState.agentName,
            action: defaultAction,
            data: {
                streaming: true
            }
        };

        return [...messages, streamingMessage];
    }, [messages, streamingUiState]);

    const showStreamingLoader =
        streamingUiState.status === "waiting-first-token";

    const hasStreamingMessage = streamingUiState.status === "streaming";

    const isEmptyPage =
        messages.length === 0 &&
        streamingUiState.status === "idle" &&
        !isLoading;

    const messagesWindow = useMemo(() => {
        if (isEmptyPage) {
            return <NoMessages />;
        }

        const lastMessage = visibleMessages[visibleMessages.length - 1];
        const isLastMessageFinal = lastMessage?.role === 'assistant' || (lastMessage?.role === 'tool' && isWidgetMessage(lastMessage?.content as string));
        const showLoader = !isLastMessageFinal;
        return (
            <MessageList
                key={sessionId}
                messages={visibleMessages}
                pending={isLoading}
                showLoader={showLoader || showStreamingLoader}
                isStreaming={hasStreamingMessage}
            />
        );
    }, [visibleMessages, isLoading, showStreamingLoader, hasStreamingMessage, isEmptyPage, sessionId]);

    const suggestions = useMemo(() => (
        <Suggestions suggestions={qs} onClick={onSuggestionClick} />
    ), [qs, onSuggestionClick]);

    const disclaimer = useMemo(() => <div style={{ textAlign: "center", color: "#5c5c5c", paddingBottom: "24px" }}>{t("chatBot.disclaimerText")}</div>, [])

    const tourAction = useCallback((actionName: string) => {
        firstTourResponse(actionName);
        setPrefData(jwt);
        if (actionName === "start") {
            startTour("tour1");
        }
    }, [firstTourResponse, setPrefData, startTour, jwt]);

    return (
        <AiAssistantContextProvider>
            <TourStart tourId={currentTourId} skipSampleQuestion={!isEmptyPage}/>
            <Modal
                toggleModal={!!displayedModal}
                closeModal={() => showModal("")}
                isCloseIcon
                closeOnBackdrop={false}
            >
                <TourLangOptions tourAction={tourAction} />
            </Modal>
            <AiAssistTemplate
                headerToggle={headerToggle}
                messagesWindow={messagesWindow}
                promptArea={promptArea}
                suggestions={suggestions}
                isEmptyPage={isEmptyPage}
                assistanceActions={<AssistanceTask />}
                manualActions={<ManualTask />}
                disclaimer={disclaimer}
            />
        </AiAssistantContextProvider>
    );
}